import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@/components/tables/Table';
import { CarModel } from '../api/carModelsApi';
import { Link } from 'react-router-dom';
import { useTableColumns } from '@/shared/hooks/useTableColumns';
import Badge from '@/components/ui/badge/Badge';
import Button from '@/components/ui/button/Button';
import { useToast } from '@/components/ui/toast/Toast';
import { useDeleteCarModel } from '../hooks/useCarModels';

interface CarModelsTableProps {
  carModels: CarModel[];
  isLoading: boolean;
}

const CarModelsTable: React.FC<CarModelsTableProps> = ({ carModels, isLoading }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { visibleColumns } = useTableColumns();
  const deleteCarModel = useDeleteCarModel();
  const isDevMode = import.meta.env.DEV;

  const handleDeleteClick = async (carModelId: string) => {
    try {
      await deleteCarModel.mutateAsync(carModelId);
      toast.success(t('car_models.delete.success'));
    } catch (error) {
      toast.error(t('car_models.delete.error'));
    }
  };

  const COLUMNS = [
    {
      key: 'image',
      header: t('car_models.columns.image'),
      field: 'image',
      cell: (row: CarModel) => (
        <div className="w-56">
          {row.image_url ? (
            <img
              src={row.image_url}
              alt={`${row.brand_name} ${row.model_name}`}
              className="w-full h-full object-cover rounded-lg"
            />
          ) : (
            <div className="w-full h-full bg-bg-tertiary rounded-lg flex items-center justify-center">
              <span className="text-text-muted">No image</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'model',
      header: t('car_models.columns.model'),
      field: 'model',
      cell: (row: CarModel) => (
        <div className="flex flex-col">
          <Link
            to={`/models/${row.id}`}
            className="font-medium hover:text-brand-500 transition-colors text-text-primary"
          >
            {row.brand_name} {row.model_name}
          </Link>
          <div className="flex gap-2 mt-1">
            <Badge size="sm" color="primary">
              {row.body_type.toUpperCase()}
            </Badge>
            <Badge size="sm" color="info">
              {row.transmission_type.toUpperCase()}
            </Badge>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('car_models.columns.status'),
      field: 'is_active',
      cell: (row: CarModel) => (
        <Badge
          size="sm"
          color={row.is_active ? 'success' : 'error'}
          variant="light"
        >
          {row.is_active ? t('car_models.status.active') : t('car_models.status.inactive')}
        </Badge>
      ),
    },
    {
      key: 'description',
      header: t('car_models.columns.description'),
      field: 'description_en',
      cell: (row: CarModel) => (
        <div className="flex flex-col gap-4">
          <div className="max-w-md">
            <h3 className="text-md font-medium text-text-secondary line-clamp-2 mb-1">
              {t('car_models.description.english')}
            </h3>
            <span className="text-sm text-text-secondary line-clamp-2 leading-[1.2]">
              {row.description_en || '—'}
            </span>
          </div>
          <div className="max-w-md">
            <h3 className="text-md font-medium text-text-secondary line-clamp-2 mb-1">
              {t('car_models.description.french')}
            </h3>
            <span className="text-sm text-text-secondary line-clamp-2 leading-[1.2]">
              {row.description_fr || '—'}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: t('car_models.columns.actions'),
      field: 'actions',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (row: CarModel) => (
        <div className="flex gap-2 justify-end" key={`actions-${row.id}`}>
          <Link
            to={`/models/${row.id}`}
            // className="px-3 py-1 text-sm text-white bg-brand-500 hover:bg-brand-600 rounded-md"
          >
            <Button
              size="sm"
              variant="primary"
              >
              {t('car_models.actions.details')}
            </Button>
          </Link>
          {isDevMode && (
            <Button
                size="sm"
              variant="danger"
              onClick={() => handleDeleteClick(row.id)}
              >
              {t('Force Delete')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Фильтруем столбцы на основе visibleColumns из хука
  const filteredColumns = useMemo(() => {
    if (visibleColumns && visibleColumns.length > 0) {
      return COLUMNS.filter(col => visibleColumns.includes(col.key));
    }
    return COLUMNS;
  }, [visibleColumns]);

  return (
    <Table<CarModel>
      data={carModels}
      columns={filteredColumns}
      loading={isLoading}
    />
  );
};

export { CarModelsTable };
